import React from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { add_shift_attributes, formatDate } from "../../utilities/helpers";
import { formatDateShift } from "../../utilities/helpers";
import { get_t_str } from "../../../../../helpers/helpers";
import ScheduleSelectedShift from "./ScheduleSelectedShift";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { Lightbox } from "react-modal-image";
import ScheduleSelectedAlsoWorkingShift from "./ScheduleSelectedAlsoWorkingShift";
import NoShiftBar from "../../shared/helpers/NoShiftBar";
import CalendarSyncPanel from "./CalendarSyncPanel";

export default function ScheduleDisplayEmployee(props) {
  const weekStartDayIndex =
    Number.isInteger(props.businessWeekStartDayIndex) &&
    props.businessWeekStartDayIndex >= 0 &&
    props.businessWeekStartDayIndex <= 6
      ? props.businessWeekStartDayIndex
      : 1;
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const orderedWeekdays = [
    ...weekdays.slice(weekStartDayIndex),
    ...weekdays.slice(0, weekStartDayIndex),
  ];

  const [lightboxImage, setLightboxImage] = React.useState(null);
  const openAttachment = (file) => {
    if (!file.presigned_url) return;
    const ext = (file.extension || "").toLowerCase();
    const isImg = ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext) // HEIC is converted to JPEG server-side; pending files render as a plain file;
    if (isImg) {
      setLightboxImage(file.presigned_url);
    } else {
      window.open(file.presigned_url, "_blank", "noopener,noreferrer");
    }
  };
  const [currentMonth, setCurrentMonth] = React.useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = React.useState(
    new Date().getFullYear()
  );
  const [selectedShift, setSelectedShift] = React.useState([]);
  const [selectedAlsoWorking, setSelectedAlsoWorking] = React.useState([]);
  const [selectedDate, setSelectedDate] = React.useState(
    formatDate(new Date())
  );

  // Reset calendar to current month when store changes
  React.useEffect(() => {
    const now = new Date();
    setCurrentMonth(now.getMonth());
    setCurrentYear(now.getFullYear());
    setSelectedDate(formatDate(now));
  }, [props.homeStore]);

  const changeMonth = (direction) => {
    let newMonth = currentMonth;
    let newYear = currentYear;
    if (direction === "next") {
      if (currentMonth === 11) {
        newMonth = 0;
        newYear = currentYear + 1;
      } else {
        newMonth = currentMonth + 1;
      }
    } else if (direction === "prev") {
      if (currentMonth === 0) {
        newMonth = 11;
        newYear = currentYear - 1;
      } else {
        newMonth = currentMonth - 1;
      }
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    if (props.onMonthChange) {
      props.onMonthChange(newMonth, newYear);
    }
  };

  const scheduleData2 = React.useMemo(() =>
    props.scheduleData?.map((v, i) => add_shift_attributes(v)),
    [props.scheduleData]
  );

  const myShifts = React.useMemo(() =>
    scheduleData2?.filter((v, i) => v.store_team_member?.user?.id === props.myUserId),
    [scheduleData2, props.myUserId]
  );

  const alsoWorking = React.useMemo(() =>
    scheduleData2?.filter((v, i) => v.store_team_member?.user?.id !== props.myUserId),
    [scheduleData2, props.myUserId]
  );

  // The payload spans multiple stores; counts describe the selected location.
  const publishedShiftCounts = React.useMemo(() => {
    const counts = {};
    (props.scheduleData || []).forEach((shift) => {
      if (
        props.homeStore != null &&
        shift.date &&
        String(shift.store?.id) === String(props.homeStore)
      ) {
        counts[shift.date] = (counts[shift.date] || 0) + 1;
      }
    });
    return counts;
  }, [props.scheduleData, props.homeStore]);

  // store_id -> store_name, derived from the (cross-store) shift payload so we
  // can label notes that come from a store other than the home store.
  const storeNamesById = React.useMemo(() => {
    const map = {};
    (scheduleData2 || []).forEach((shift) => {
      if (shift?.store?.id != null) map[shift.store.id] = shift.store.store_name;
    });
    return map;
  }, [scheduleData2]);

  // Daily notes (manager events shared with employees) keyed by date string.
  // The backend already filters these to visible_to_employees=True for employees.
  // Two sources, deduped by event id:
  //   1. props.eventsData — the HOME store's notes, on every day.
  //   2. each of my shifts' daily_events — the SHIFT store's notes, on the days
  //      I actually work there (covers cross-store shifts, matching "My Day").
  const eventsByDate = React.useMemo(() => {
    const map = {};
    const seenByDate = {};
    const addEvent = (event) => {
      if (!event?.date) return;
      // Dedupe by event id (the globally-unique PK); fall back to a
      // store-scoped composite so two different-store notes never collide.
      const key =
        event.id ??
        `${event.store}|${event.title}|${event.start_time}|${event.end_time}`;
      if (!seenByDate[event.date]) seenByDate[event.date] = new Set();
      if (seenByDate[event.date].has(key)) return;
      seenByDate[event.date].add(key);
      (map[event.date] = map[event.date] || []).push(event);
    };
    (props.eventsData || []).forEach(addEvent);
    (myShifts || []).forEach((shift) =>
      (shift?.daily_events || []).forEach(addEvent)
    );
    return map;
  }, [props.eventsData, myShifts]);

  // The store name to show for a note when it's NOT from the home store, so a
  // multi-store employee can tell which location a note belongs to. Returns
  // null for home-store notes (and when homeStore/store can't be resolved).
  const eventStoreLabel = (event) => {
    if (props.homeStore == null) return null;
    if (String(event?.store) === String(props.homeStore)) return null;
    return storeNamesById[event?.store] || null;
  };

  // Holidays (global) keyed by date string.
  const holidaysByDate = React.useMemo(() => {
    const map = {};
    (props.holidaysData || []).forEach((holiday) => {
      if (!holiday?.date) return;
      (map[holiday.date] = map[holiday.date] || []).push(holiday);
    });
    return map;
  }, [props.holidaysData]);

  const [days, setDays] = React.useState([]);

  React.useEffect(() => {
    function isDateInCurrentMonth(date) {
      return (
        date.getMonth() === currentMonth && date.getFullYear() === currentYear
      );
    }

    const today = new Date(); // Get the current date

    // Calculate the first day of the current month
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

    // Calculate the number of days in the previous month to display
    const daysInPrevMonth = (firstDayOfMonth - weekStartDayIndex + 7) % 7;

    // Determine if we need 5 or 6 weeks
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const lastDayPosition = daysInPrevMonth + daysInMonth - 1;
    const totalDays = lastDayPosition >= 35 ? 42 : 35;

    // Transform the received data and add the currentMonth field
    const transformedData = [];
    const currentDate = new Date(
      currentYear,
      currentMonth,
      1 - daysInPrevMonth
    ); // Start with the first day to display
    let initialSelectedShift = {};
    let initialSelectedAlsoWorking = [];

    for (let i = 0; i < totalDays; i++) {
      // Use formatDate (local calendar date) to match backend date strings
      // (shift/event/holiday .date are local "YYYY-MM-DD") and selectedDate.
      // toISOString() would shift to UTC and roll the day for positive-offset
      // timezones, breaking date-keyed lookups.
      const date = formatDate(currentDate);
      const isInCurrentMonth = isDateInCurrentMonth(currentDate);

      // Check if the current date matches the loop date
      const isSelected = formatDate(currentDate) === selectedDate;

      // console.log(formatDate(new Date()));
      // console.log(formatDate(currentDate));

      // Find the corresponding shift data in the receivedData, if it exists
      const shiftData = myShifts?.filter((shift) => shift.date === date);

      const alsoWorkingData =
        alsoWorking?.filter((day) => day.date === date) || [];

      // Calculate if the current date is today's date
      const isToday =
        currentDate.getFullYear() === today.getFullYear() &&
        currentDate.getMonth() === today.getMonth() &&
        currentDate.getDate() === today.getDate();

      // if (isSelected) {
      //   // Set initialSelectedShift and initialSelectedAlsoWorking for the current day
      //   initialSelectedShift = shiftData ? [ ...shiftData ] : {};
      //   initialSelectedAlsoWorking = [...alsoWorkingData];
      // }

      transformedData.push({
        date,
        shift: shiftData ? [...shiftData] : null,
        alsoWorking: alsoWorkingData ? [...alsoWorkingData] : [],
        isCurrentMonth: isInCurrentMonth,
        isSelected, // Set isSelected based on the current date
        isToday,
      });

      // Move to the next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Set the initial values for selectedShift and selectedAlsoWorking
    // setSelectedShift(initialSelectedShift);
    // setSelectedAlsoWorking(initialSelectedAlsoWorking);

    // Set the state with the transformed data
    setDays(transformedData);
  }, [currentMonth, currentYear, myShifts, alsoWorking, selectedDate, weekStartDayIndex]);

  React.useEffect(() => {
    // Only continue if all dependencies are available
    if (!myShifts || !alsoWorking || !selectedDate) return;

    const shiftData = myShifts?.filter((shift) => shift.date === selectedDate);
    const shiftStore = shiftData?.length > 0 ? shiftData?.[0]?.store?.id : props.homeStore;

    const alsoWorkingData = alsoWorking?.filter(
      (day) => day.date === selectedDate && day.store?.id === shiftStore
    ) || [];

    // Simple referential check for arrays - if empty use empty array
    setSelectedShift(shiftData || []);
    setSelectedAlsoWorking(alsoWorkingData || []);

  }, [selectedDate, myShifts, alsoWorking, props.homeStore]);

  // console.log(selectedShift);

  // Modify the selectDate function to filter alsoWorking by store
  const selectDate = (dayIdx, shiftInfo, alsoWorking) => {
    const newDays = days?.map((day, index) => {
      if (index === dayIdx) {
        return { ...day, isSelected: true };
      } else {
        return { ...day, isSelected: false };
      }
    });
    setDays(newDays);

    // Get the selected date
    const selectedDate = days[dayIdx].date;
    setSelectedDate(selectedDate);

    if (shiftInfo && shiftInfo.length > 0) {
      setSelectedShift([...shiftInfo]);

      // Get the store ID for the selected shift
      const shiftStore = shiftInfo[0]?.store?.id;

      // Filter alsoWorking to only include people at the same store
      const filteredAlsoWorking = alsoWorking?.filter(shift => shift.store?.id === shiftStore) || [];
      setSelectedAlsoWorking(filteredAlsoWorking);
    } else {
      // If no shift is associated with the selected date, reset selectedShift
      setSelectedShift([]);

      // If there's no shift, filter alsoWorking by the home store
      const filteredAlsoWorking = alsoWorking?.filter(shift => shift.store?.id === props.homeStore) || [];
      setSelectedAlsoWorking(filteredAlsoWorking);
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }

  let notMyUpcomingShifts = props.notMyUpcomingShifts?.map((v, i) =>
    add_shift_attributes(v)
  );

  function isDateInPast(dateStr) {
    const currentDate = new Date();
    // Compare the date objects and return the result as a boolean
    return dateStr < formatDate(currentDate);
  }

  return (
    <div>
      {lightboxImage && (
        <div className="absolute w-screen h-screen z-50">
          <Lightbox
            small={lightboxImage}
            medium={lightboxImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setLightboxImage(null)}
          />
        </div>
      )}
      {/* <RequestHeader title="Schedule" /> */}
      <div>
        <div className="w-full flex mt-12 px-12">
          <div className="text-center w-[68%] border-r border-slate-200 pr-8 pb-8">
            <div className="w-full flex justify-center items-center">
              <div className="flex items-center text-slate-900 w-[50%] margin-auto">
                <button
                  type="button"
                  className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-slate-500 hover:text-slate-600"
                  onClick={() => changeMonth("prev")}
                >
                  <span className="sr-only">Previous month</span>
                  <ChevronLeftIcon />
                </button>
                <div className="flex-auto font-semibold text-lg">
                  {months[currentMonth]} {currentYear}
                </div>
                <button
                  type="button"
                  className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-slate-500 hover:text-slate-600"
                  onClick={() => changeMonth("next")}
                >
                  <span className="sr-only">Next month</span>
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-violet-600" aria-hidden="true" />
                Your shifts
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-600" aria-hidden="true" />
                Published shifts at selected location
              </span>
            </div>
            <div className="mt-6 grid grid-cols-7 text-xs leading-6 text-slate-500">
              {orderedWeekdays.map(
                (dayOfWeek) => (
                  <div key={dayOfWeek}>{dayOfWeek}</div>
                )
              )}
            </div>
            <div className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-slate-200 text-sm shadow ring-1 ring-slate-200">
              {days?.map((day, dayIdx) => (
                <button
                  key={dayIdx}
                  type="button"
                  aria-pressed={day.isSelected}
                  className={classNames(
                    "min-h-[110px] p-2 hover:bg-slate-100 focus:z-10 relative flex flex-col items-stretch text-left align-top",
                    day.isCurrentMonth ? "bg-white" : "bg-slate-50",
                    dayIdx === 0 && "rounded-tl-lg",
                    dayIdx === 6 && "rounded-tr-lg",
                    dayIdx === days.length - 7 && "rounded-bl-lg",
                    dayIdx === days.length - 1 && "rounded-br-lg"
                  )}
                  onClick={() => selectDate(dayIdx, day.shift, day.alsoWorking)}
                >
                  {/* Day number */}
                  <div className="mb-1 flex items-center justify-between">
                    <time
                      dateTime={day.date}
                      className={classNames(
                        "flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold",
                        day.isSelected && "bg-violet-700 text-white",
                        !day.isSelected && day.isToday && "text-violet-700",
                        !day.isSelected &&
                          !day.isToday &&
                          day.isCurrentMonth &&
                          "text-slate-900",
                        !day.isSelected &&
                          !day.isToday &&
                          !day.isCurrentMonth &&
                          "text-slate-300"
                      )}
                    >
                      {day.date.split("-").pop().replace(/^0/, "")}
                    </time>
                  </div>

                  {publishedShiftCounts[day.date] > 0 && (
                    <div
                      className="mb-1 self-start rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold leading-tight text-emerald-700 ring-1 ring-inset ring-emerald-200"
                      title={`${publishedShiftCounts[day.date]} published ${publishedShiftCounts[day.date] === 1 ? "shift" : "shifts"} at selected location`}
                    >
                      {publishedShiftCounts[day.date]} published
                    </div>
                  )}

                  {/* Your shift(s) */}
                  {day.shift?.length > 0 && (
                    <div
                      className={`mb-1 space-y-1 ${
                        isDateInPast(day.date) ? "opacity-60" : ""
                      }`}
                    >
                      {day.shift.slice(0, 2).map((shift, idx) => (
                        <div
                          key={shift.id ?? idx}
                          className="text-[9px] leading-tight px-1.5 py-1 bg-violet-50 border border-violet-200 text-violet-700 rounded"
                          title="Your shift"
                        >
                          <p className="truncate font-semibold">
                            {shift?.shift_start_formatted?.[0]}
                            {shift?.shift_start_formatted?.[1]
                              ?.charAt(0)
                              ?.toLowerCase()}
                            –{shift?.shift_end_formatted?.[0]}
                            {shift?.shift_end_formatted?.[1]
                              ?.charAt(0)
                              ?.toLowerCase()}
                          </p>
                          {shift?.location && (
                            <p className="truncate text-violet-500 mt-0.5">
                              {shift.location}
                            </p>
                          )}
                        </div>
                      ))}
                      {day.shift.length > 2 && (
                        <div className="text-[9px] px-1.5 text-violet-600 font-semibold">
                          +{day.shift.length - 2} more
                        </div>
                      )}
                    </div>
                  )}

                  {/* Holidays */}
                  {holidaysByDate[day.date]?.length > 0 && (
                    <div className="mb-1 space-y-1">
                      {holidaysByDate[day.date].map((holiday, idx) => (
                        <div
                          key={holiday.uuid ?? idx}
                          className="text-[9px] px-1.5 py-1 bg-amber-50 border border-amber-200 text-amber-700 rounded flex items-center gap-1"
                          title={holiday.name}
                        >
                          <EventOutlinedIcon sx={{ fontSize: "10px" }} />
                          <span className="truncate font-semibold flex-1">
                            {holiday.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Notes */}
                  {eventsByDate[day.date]?.length > 0 && (
                    <div className="space-y-1">
                      {eventsByDate[day.date].slice(0, 2).map((event, idx) => (
                        <div
                          key={event.id ?? idx}
                          className="text-[9px] px-1.5 py-1 bg-white border border-violet-200 text-violet-700 rounded flex items-center gap-1"
                          title={
                            eventStoreLabel(event)
                              ? `${event.title || "Untitled note"} · ${eventStoreLabel(
                                  event
                                )}`
                              : event.title || "Untitled note"
                          }
                        >
                          <NoteAltOutlinedIcon sx={{ fontSize: "10px" }} />
                          <span className="truncate font-semibold flex-1">
                            {event.title && event.title.trim().length > 0
                              ? event.title
                              : "Untitled note"}
                          </span>
                          {event.attachments?.length > 0 && (
                            <AttachFileIcon sx={{ fontSize: "10px" }} className="shrink-0" />
                          )}
                        </div>
                      ))}
                      {eventsByDate[day.date].length > 2 && (
                        <div className="text-[9px] px-1.5 text-violet-600 font-semibold">
                          +{eventsByDate[day.date].length - 2} more
                        </div>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-8">
              <CalendarSyncPanel
                employeeScheduleSyncHash={props.employeeScheduleSyncHash}
              />
            </div>
          </div>
          <div className="flex-1 pl-8">
            <div>
              <h3 className="text-xl font-bold text-slate-900">
                {formatDateShift(selectedDate)}
              </h3>
            </div>
            {holidaysByDate[selectedDate]?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {holidaysByDate[selectedDate].map((holiday, index) => (
                  <div
                    key={holiday.uuid ?? index}
                    className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-3 py-1"
                  >
                    <EventOutlinedIcon sx={{ fontSize: 16, color: "#b45309" }} />
                    <p className="text-sm font-semibold text-amber-700">{holiday.name}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3">
              {selectedShift?.length > 0 ? (
                <div>
                  {selectedShift?.map((shift, index) => (
                    <ScheduleSelectedShift
                      key={index}
                      index={index}
                      firstName={shift?.store_team_member?.first_name}
                      lastName={shift?.store_team_member?.last_name}
                      date={shift?.date}
                      dateFormatted={formatDateShift(shift?.date)}
                      isDateInPast={isDateInPast(shift?.date)}
                      shiftStartNumber={shift?.shift_start_formatted?.[0]}
                      shiftStartLetters={shift?.shift_start_formatted?.[1]}
                      shiftEndNumber={shift?.shift_end_formatted?.[0]}
                      shiftEndLetters={shift?.shift_end_formatted?.[1]}
                      role={shift?.role}
                      station={shift?.station?.name}
                      roleType={shift?.roleType}
                      roleId={shift?.role_id}
                      location={shift?.location}
                      shift={shift}
                      break={shift?.break_formatted}
                      notMyUpcomingShifts={notMyUpcomingShifts}
                      drop_shift={props.drop_shift}
                      trade_shift={props.trade_shift}
                      rescind_drop_shift={props.rescind_drop_shift}
                      rescind_trade_shift={props.rescind_trade_shift}
                      roleNames={props.roleNames}
                      note={shift?.note}
                    />
                  ))}
                </div>
              ) : (
                <div className="mt-3">
                  <NoShiftBar text="You have no shift scheduled!" />
                </div>
              )}
            </div>
            {eventsByDate[selectedDate]?.length > 0 && (
              <div className="mt-5">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider pb-2">
                  Notes
                </p>
                <div className="mt-1">
                  {eventsByDate[selectedDate].map((event, index) => (
                  <div
                    key={event.id ?? index}
                    className="mb-2 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5"
                  >
                    <div className="flex items-center gap-2">
                      <NoteAltOutlinedIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
                      <p className="text-sm font-semibold text-slate-900">
                        {event.title && event.title.trim().length > 0
                          ? event.title
                          : "Untitled note"}
                        {eventStoreLabel(event) && (
                          <span className="text-xs font-medium text-slate-400">
                            {" "}
                            · {eventStoreLabel(event)}
                          </span>
                        )}
                      </p>
                    </div>
                    {event.description && (
                      <p className="text-xs text-slate-500 mt-0.5">{event.description}</p>
                    )}
                    {event.start_time != null && (
                      <p className="text-xs text-violet-600 mt-0.5">
                        {get_t_str(event.start_time)}
                        {event.end_time != null && ` - ${get_t_str(event.end_time)}`}
                      </p>
                    )}
                    {event.attachments?.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {event.attachments.map((file) => {
                          const ext = (file.extension || "").toLowerCase();
                          const isImg = ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext) // HEIC is converted to JPEG server-side; pending files render as a plain file;
                          return (
                            <button
                              key={file.uuid}
                              onClick={() => openAttachment(file)}
                              className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-violet-700 w-full text-left"
                            >
                              {isImg ? (
                                <img
                                  src={file.presigned_url}
                                  alt={file.displayName || file.name}
                                  className="w-8 h-8 rounded object-cover shrink-0"
                                />
                              ) : (
                                <InsertDriveFileIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                              )}
                              <span className="truncate">{file.displayName || file.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-5">
              <div className="flex items-center justify-between pb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Also Working</p>
                <p className="text-xs text-slate-400">
                  {selectedAlsoWorking?.length}
                </p>
              </div>
              <div className="mt-1">
                {selectedAlsoWorking && selectedAlsoWorking?.length > 0 ? (
                  selectedAlsoWorking?.map((shift, index) => (
                    <ScheduleSelectedAlsoWorkingShift
                      key={index}
                      name={`${shift?.store_team_member?.first_name} ${shift?.store_team_member?.last_name}`}
                      shiftStartNumber={shift?.shift_start_formatted?.[0]}
                      shiftStartLetters={shift?.shift_start_formatted?.[1]}
                      shiftEndNumber={shift?.shift_end_formatted?.[0]}
                      shiftEndLetters={shift?.shift_end_formatted?.[1]}
                      role={shift?.role}
                      station={shift?.station?.name}
                      roleType={shift?.role_type}
                      roleId={shift?.role_id}
                      roleNames={props.roleNames}
                    />
                  ))
                ) : (
                  <div className="flex items-center space-x-3 mt-2">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                      <InfoOutlinedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                    </div>
                    <p className="text-sm text-slate-400">No one else is scheduled today</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
